import type { DateSelectorPresetId } from '@app/components/date-range-selector/date-selector-presets';
import { getPresetIdFromDate } from '@app/components/date-range-selector/date-selector-presets';
import { SingleDateSelector } from '@app/components/date-range-selector/SingleDateSelector';
import { useTimezone } from '@app/core/hooks';
import { usePostHogCapture } from '@app/core/integrations/posthog/usePostHogCapture';
import { setDateKeepingTime } from '@mi-front/core';
import { Box, ButtonProps } from '@mui/material';
import moment from 'moment-timezone';
import { useStationTimeMoveRange } from '../../../../hooks/navigation/useStationTimeMoveRange';
import { useLoadRangeAtTs } from '../../../../hooks/range/useLoadRangeAtTs';
import { useStreamUserDefinedRange } from '../../../../stores/stream-screen.store';

export interface Props {
  isDisabled?: boolean;
}

/**
 * Date selector for the stream range. Uses stream range start (from) as the current
 * date; changing it builds a new timestamp in the station timezone and loads that range.
 * Dates before the station retention (allowed.from) are disabled.
 */
export function DateControl({ isDisabled = false }: Props) {
  const capture = usePostHogCapture();
  const tz = useTimezone();
  const loadRangeAtTs = useLoadRangeAtTs();
  const allowed = useStationTimeMoveRange();

  const { from } = useStreamUserDefinedRange();
  const fromDate = from ? new Date(from) : null;

  const dateValue = !fromDate || !tz ? null : moment(fromDate).tz(tz).startOf('day').toDate();

  const presetId = !dateValue || !tz ? null : getPresetIdFromDate(dateValue, tz);

  const minDate = new Date(allowed.from);
  const dayPickerDisabled = {
    before: minDate,
    after: new Date(),
  };

  const onDateChange = (date: Date, _newPresetId: DateSelectorPresetId) => {
    if (!tz) {
      return;
    }

    capture('logger:date_changed', {
      old_date: fromDate?.toLocaleDateString(),
      new_date: date.toLocaleDateString(),
      preset: _newPresetId,
    });

    loadRangeAtTs(setDateKeepingTime(from, date, tz).valueOf());
  };

  return (
    <Box>
      <SingleDateSelector
        value={dateValue ?? undefined}
        presetId={presetId ?? undefined}
        onChange={onDateChange}
        dayPickerDisabled={dayPickerDisabled}
        buttonProps={{
          size: 'large',
          variant: 'outlined',
          color: 'secondary.dark' as ButtonProps['color'],
          disabled: isDisabled,
          sx: {
            textTransform: 'none',
            width: '17ch',
            '.MuiSvgIcon-root': {
              fill: isDisabled ? undefined : '#4b5157',
            },
          },
        }}
      />
    </Box>
  );
}
